<template>
  <div class="projects-section">
    <div class="section-header">
      <div class="section-title">
        <h3>Projektek</h3>
        <span class="section-count">{{ projectCount }} közelgő projekt</span>
      </div>
      <div class="section-actions">
        <!-- <button class="btn btn-primary btn-sm" @click="createNewProject">
          <i class="fas fa-plus"></i>
          Új projekt
        </button> -->
      </div>
    </div>

    <div class="section-content">
      <div v-if="loading" class="loading-spinner">
        <i class="fas fa-spinner fa-spin"></i>
        <span>Projektek betöltése...</span>
      </div>
      <div v-else-if="error" class="error-state">
        <i class="fas fa-exclamation-triangle"></i>
        <p>Hiba történt a projektek betöltése során</p>
        <button class="btn btn-primary btn-sm" @click="loadProjects">
          <i class="fas fa-redo"></i>
          Újrapróbálás
        </button>
      </div>
      <div v-else-if="projects.length === 0" class="empty-state">
        <i class="fas fa-inbox"></i>
        <p>Nincs aktív projekt</p>
      </div>
      <div v-else class="row">
        <!-- Bal oldal: Projektek lista -->
        <div class="col">
          <div v-for="project in projects" :key="project.id" class="project-item" @click="viewProject(project.id)">
            <div class="project-info">
              <h6 class="project-title">{{ project.name }}</h6>
              <div class="project-details">
                <span class="project-code">{{ project.project_code }}</span>
                <span class="project-date">
                  <i class="fas fa-calendar-alt"></i>
                  {{ formatDate(project.farthest_deadline) }}
                </span>
              </div>
            </div>
            <div class="project-actions">
              <button class="btn btn-outline-primary btn-sm" @click.stop="viewProject(project.id)">
                <i class="fas fa-eye"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- Jobb oldal: Naptár nézet -->
        <div class="col" >
          <div class="calendar-header">
            <button class="btn btn-sm btn-outline-secondary" @click="previousMonth">
              <i class="fas fa-chevron-left"></i>
            </button>
            <h5>{{ currentMonthName }}</h5>
            <button class="btn btn-sm btn-outline-secondary" @click="nextMonth">
              <i class="fas fa-chevron-right"></i>
            </button>
          </div>
          <div class="calendar-grid">
            <div class="calendar-weekday" v-for="day in weekDays" :key="day">{{ day }}</div>
            <div
              v-for="day in calendarDays"
              :key="day.date"
              class="calendar-day"
              :class="{
                'other-month': !day.isCurrentMonth,
                'today': day.isToday,
                'has-deadline': day.projects.length > 0
              }"
            >
              <div class="day-number">{{ day.day }}</div>
              <div v-if="day.projects.length > 0" class="day-projects">
                <div
                  v-for="project in day.projects"
                  :key="project.id"
                  class="day-project"
                  :title="project.name"
                  @click="viewProject(project.id)"
                >
                  {{ project.name }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div class="section-footer" v-if="!loading && !error && projects.length > 0">
        <router-link to="/projects" class="btn btn-outline-primary">
          <i class="fas fa-eye me-2"></i>
          {{ notShownProjects > 0 ? `További ${notShownProjects}` : 'Összes projekt' }}
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import apiService from '@/services/apiService';
import authService from '@/services/authService';

export default {
  name: 'ProjectsCard',
  data() {
    return {
      projects: [],
      projectCount: 0,
      notShownProjects: 0,
      loading: false,
      error: false,
      currentMonth: new Date().getMonth(),
      currentYear: new Date().getFullYear(),
      weekDays: ['H', 'K', 'Sz', 'Cs', 'P', 'Sz', 'V']
    }
  },
  computed: {
    isTester() {
      return authService.isTester();
    },
    currentMonthName() {
      const date = new Date(this.currentYear, this.currentMonth);
      return date.toLocaleDateString('hu-HU', { year: 'numeric', month: 'long' });
    },
    calendarDays() {
      const firstDay = new Date(this.currentYear, this.currentMonth, 1);
      const lastDay = new Date(this.currentYear, this.currentMonth + 1, 0);
      const firstDayOfWeek = (firstDay.getDay() + 6) % 7; // Convert to Monday = 0
      const daysInMonth = lastDay.getDate();
      
      const days = [];
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Previous month days
      const prevMonthLastDay = new Date(this.currentYear, this.currentMonth, 0).getDate();
      for (let i = firstDayOfWeek - 1; i >= 0; i--) {
        const day = prevMonthLastDay - i;
        days.push({
          day,
          date: new Date(this.currentYear, this.currentMonth - 1, day),
          isCurrentMonth: false,
          isToday: false,
          projects: []
        });
      }

      // Current month days
      for (let i = 1; i <= daysInMonth; i++) {
        const date = new Date(this.currentYear, this.currentMonth, i);
        const dateString = this.formatDateForComparison(date);
        const isToday = date.getTime() === today.getTime();
        
        const projectsForDay = this.projects.filter(project => {
          if (!project.farthest_deadline) return false;
          const projectDate = this.formatDateForComparison(new Date(project.farthest_deadline));
          return projectDate === dateString;
        });

        days.push({
          day: i,
          date,
          isCurrentMonth: true,
          isToday,
          projects: projectsForDay
        });
      }

      // Next month days
      const remainingDays = 42 - days.length; // 6 rows * 7 days
      for (let i = 1; i <= remainingDays; i++) {
        days.push({
          day: i,
          date: new Date(this.currentYear, this.currentMonth + 1, i),
          isCurrentMonth: false,
          isToday: false,
          projects: []
        });
      }

      return days;
    }
  },
  async mounted() {
    await this.loadProjects();
  },
  methods: {
    async loadProjects() {
      try {
        this.loading = true;
        this.error = false;

        const response = await apiService.get('/projects/upcoming-projects?top=10');

        if (response.success && response.data) {
          this.projects = response.data.upcoming_projects || [];
          this.projectCount = response.data.total_projects || 0;
          this.notShownProjects = response.data.not_shown_projects || 0;
        } else {
          throw new Error('Failed to load projects');
        }
      } catch (error) {
        console.error('Error loading projects:', error);
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleDateString('hu-HU');
    },
    formatDateForComparison(date) {
      return date.toISOString().split('T')[0];
    },
    previousMonth() {
      if (this.currentMonth === 0) {
        this.currentMonth = 11;
        this.currentYear--;
      } else {
        this.currentMonth--;
      }
    },
    nextMonth() {
      if (this.currentMonth === 11) {
        this.currentMonth = 0;
        this.currentYear++;
      } else {
        this.currentMonth++;
      }
    },
    viewProject(projectId) {
      this.$router.push(`/projects/${projectId}`);
    },
    createNewProject() {
      this.$router.push('/projects/create');
    }
  }
}
</script>

<style scoped>
.projects-section {
  margin-bottom: 2rem;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.section-title h3 {
  font-size: 1.25rem;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
  margin-bottom: 0.25rem;
}

.section-count {
  font-size: 0.875rem;
  color: #6c757d;
}

.section-actions {
  display: flex;
  gap: 0.5rem;
}

.section-content {
  background: transparent;
}

.projects-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
}

.projects-list {
  overflow-y: auto;
  padding-top: 10px;
  max-height: 600px;
}

.project-item {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 0.75rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.project-item:hover {
  border-color: #1d4699;
  box-shadow: 0 5px 20px rgba(29, 70, 153, 0.15);
  transform: translateY(-2px);
}

.project-item:last-child {
  margin-bottom: 0;
}

.project-info {
  flex: 1;
  min-width: 0;
  margin-right: 1rem;
}

.project-title {
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  color: #2c3e50;
  line-height: 1.3;
}

.project-details {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.project-code {
  font-size: 0.75rem;
  color: #6c757d;
  background-color: #f8f9fa;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-family: monospace;
}

.project-date {
  font-size: 0.75rem;
  color: #1d4699;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.project-actions {
  flex-shrink: 0;
}

/* Calendar styles */
.calendar-view {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.calendar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.calendar-header h5 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #2c3e50;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.25rem;
}

.calendar-weekday {
  text-align: center;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6c757d;
  padding: 0.5rem;
}

.calendar-day {
  min-height: 60px;
  padding: 0.25rem;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  background: #f8f9fa;
  transition: all 0.2s ease;
}

.calendar-day.other-month {
  opacity: 0.4;
}

.calendar-day.today {
  background: #e7f1ff;
  border-color: #1d4699;
}

.calendar-day.has-deadline {
  background: #fff3cd;
  border-color: #ffc107;
}

.day-number {
  font-size: 0.75rem;
  font-weight: 600;
  color: #2c3e50;
  text-align: right;
  margin-bottom: 0.25rem;
}

.day-projects {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.day-project {
  font-size: 0.625rem;
  padding: 0.125rem 0.25rem;
  background: #1d4699;
  color: white;
  border-radius: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  transition: background 0.2s ease;
}

.day-project:hover {
  background: #0d2f5e;
}

.section-footer {
  margin-top: 1.5rem;
  text-align: center;
}

.loading-spinner,
.error-state,
.empty-state {
  text-align: center;
  padding: 2rem;
  color: #6c757d;
}

.loading-spinner i,
.error-state i,
.empty-state i {
  font-size: 2rem;
  margin-bottom: 1rem;
}

.loading-spinner {
  color: #1d4699;
}

.loading-spinner i {
  animation: spin 1s linear infinite;
}

.error-state {
  color: #dc3545;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Responsive */
@media (max-width: 1200px) {
  .projects-container {
    grid-template-columns: 1fr;
  }
  
  .calendar-view {
    order: -1;
  }
}

@media (max-width: 768px) {
  .section-header {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }
  
  .project-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
  
  .project-info {
    margin-right: 0;
  }
  
  .project-actions {
    align-self: flex-end;
  }

  .calendar-day {
    min-height: 50px;
  }

  .day-number {
    font-size: 0.7rem;
  }

  .day-project {
    font-size: 0.55rem;
  }
}
</style>